import { type JSX } from "react";
import { useRouteError, isRouteErrorResponse } from "react-router-dom";

// Router-level error boundary. React Router renders this instead of the raw
// "Unexpected Application Error!" screen whenever a route (including lazy chunk
// loading) throws. A stale/mismatched asset bundle after a deploy is the most
// common trigger, so we offer a hard reload as the primary recovery action.
export function Component(): JSX.Element {
    const error = useRouteError();

    let message = "An unexpected error occurred.";
    if (isRouteErrorResponse(error)) {
        message = `${error.status} ${error.statusText}`;
    } else if (error instanceof Error) {
        message = error.message;
    }

    const reload = async () => {
        // A stale/mismatched asset bundle is the usual cause of this screen. A
        // plain location.reload() will not re-fetch assets served with
        // "Cache-Control: immutable", so proactively clear caches and any
        // service worker registrations before doing a hard reload.
        try {
            if ("caches" in window) {
                const keys = await caches.keys();
                await Promise.all(keys.map(key => caches.delete(key)));
            }
            if ("serviceWorker" in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                await Promise.all(registrations.map(registration => registration.unregister()));
            }
        } catch {
            // Ignore cache-clearing failures and fall through to the reload.
        }
        // Cache-bust the document URL so the browser fetches a fresh index.html
        // (and therefore the current, matching asset hashes).
        const url = new URL(window.location.href);
        url.searchParams.set("_", Date.now().toString());
        window.location.replace(url.toString());
    };

    return (
        <div
            role="alert"
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "100vh",
                padding: "2rem",
                textAlign: "center",
                fontFamily: 'Roboto, "Segoe UI", -apple-system, BlinkMacSystemFont, "Helvetica Neue", sans-serif',
                color: "#1D0638"
            }}
        >
            <h1 style={{ marginBottom: "0.5rem" }}>Something went wrong</h1>
            <p style={{ marginBottom: "1.5rem", color: "#373741" }}>
                The application ran into a problem. This can happen right after an update when your browser is still using an outdated version. Reloading
                usually fixes it.
            </p>
            <button
                type="button"
                onClick={reload}
                style={{
                    background: "#442EE0",
                    color: "#FFFFFF",
                    border: "none",
                    borderRadius: "6px",
                    padding: "0.75rem 1.5rem",
                    fontSize: "1rem",
                    cursor: "pointer"
                }}
            >
                Reload page
            </button>
            <pre
                style={{
                    marginTop: "1.5rem",
                    maxWidth: "40rem",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: "#8a8a90",
                    fontSize: "0.8rem"
                }}
            >
                {message}
            </pre>
        </div>
    );
}

Component.displayName = "ErrorPage";
